import { Utils } from '@genial/utils';
import React from 'react';

import { OperationType } from '../../../../components/OperationType';
import { EQUITY_ORDER_TYPE_LABEL } from '../../../BatchTicket/components/DynamicAsset/components/Asset/products/Equity/ListItem';
import { OperationType as OrderOperationType } from '../../../BatchTicket/components/DynamicAsset/contexts/constants';
import { formatAvailableAmount } from '../../../NewApplication/Product/components/FixedIncome/constants';
import { DeviceType, messageError, type ProductType } from '../../utils';
import { EquityLastPrice } from '../EquityLastPrice';
import { EquityMobileTitle } from '../EquityMobileTitle';
import { EquityProductDisplay } from '../EquityProductDisplay';
import { ProductTitle } from '../ProductTitle';
import { RiskRatingTooltip } from '../RiskRatingTooltip';
import { ValueWithSubtitle } from '../ValueWithSubtitle';

import { ProductSectionProps } from '.';

type createProductSectionProps = ProductSectionProps & {
  orderAmountFormatted: string;
  IsSellOperation: boolean;
  isMobileDevice: boolean;
};

export const lateralBarColor = {
  FixedIncome: 'product.rf',
  Fund: 'product.fundos',
  Equity: {
    sell: 'product.rendavariavelsale',
    buy: 'product.rendavariavelbuy',
  },
  DirectTreasury: 'product.tesouro',
  StructuredOperations: 'product.estruturados',
};

export const getBackgroundColor = (productType: ProductType, transactionType: 'buy' | 'sell') => {
  if (productType === 'Equity') {
    return lateralBarColor[productType][transactionType] ?? 'product.rendavariavel';
  }
  return lateralBarColor[productType];
};

export const createSectionProductItems = ({
  device,
  ...props
}: ProductSectionProps & DeviceType) => {
  const orderAmount = props?.orderAmount ? Utils.Mask.Money(props?.orderAmount) : messageError;
  const IsSellOperation = props?.operation === 'sell';
  const isMobileDevice = device === 'mobile';
  const data = { ...props, orderAmountFormatted: orderAmount, isMobileDevice, IsSellOperation };
  switch (props.productType) {
    case 'FixedIncome':
      return createFixedIncomeSection(data);
    case 'Fund':
      return createFundSection(data);
    case 'Equity':
      return createEquitySection(data);
    case 'DirectTreasury':
      return createDirectTreasurySection(data);
    case 'StructuredOperations':
      return createStructuredOperationsSection(data);
    default:
      return [];
  }
};

const createFixedIncomeSection = (props: createProductSectionProps) => {
  const riskRatingLevelComponent = props?.riskRatingLevel ? (
    <RiskRatingTooltip label={props?.riskRatingName} value={props?.riskRatingLevel} />
  ) : (
    messageError
  );

  const yearlyProfitability =
    props.fullProductItem?.orderFee?.profitabilityYearly || props?.profitabilityYearly;
  const toMaturityProfitability =
    props.fullProductItem?.orderFee?.profitabilityToMaturity || props?.profitabilityToMaturity;

  const profitabilityFormatted =
    props.fullProductItem?.orderFee?.profitability || props?.profitabilityFormatted || messageError;

  const profitabilityYearlyFormatted = yearlyProfitability
    ? Utils.Mask.Percentage(yearlyProfitability)
    : messageError;

  const profitabilityToMaturityFormatted = toMaturityProfitability
    ? Utils.Mask.Percentage(toMaturityProfitability)
    : messageError;

  const defaultUnitPriceFormatted = props.unitPrice
    ? Utils.Mask.Money(props?.unitPrice)
    : messageError;

  const unitPriceFormatted =
    props.fullProductItem?.orderFee?.customerPuAmountFormatted || defaultUnitPriceFormatted;

  const profitabilityComponent = (
    <ValueWithSubtitle
      value={profitabilityYearlyFormatted}
      valueDescription="Anual"
      subtitle={profitabilityToMaturityFormatted}
      subtitleDescription="- Até o venc."
    />
  );

  const maturityDateFormatted =
    props.fullProductItem?.maturityDateNormalized ||
    Utils.Mask.DateFormat(props?.maturityDate, 'dd/MM/yyyy');

  const dueDateFormatted =
    props.fullProductItem?.dueDateNormalized ||
    (props?.dueDate ? Utils.Mask.DateFormat(props?.dueDate, 'dd/MM/yyyy') : undefined);

  const timelinesComponent = (
    <ValueWithSubtitle
      value={maturityDateFormatted}
      valueDescription="Vencimento"
      subtitle={dueDateFormatted}
      subtitleDescription={dueDateFormatted ? '- Carência' : undefined}
    />
  );

  const spreadFeeFormatted = props.fullProductItem?.orderFee?.honorariumFee || messageError;

  const spreadFeeComponent = (
    <ValueWithSubtitle
      value={spreadFeeFormatted}
      subtitle={
        props.fullProductItem?.estimatedIncomeCalculated
          ? `Receita Estim.: ${Utils.Mask.Money(props.fullProductItem.estimatedIncomeCalculated)}`
          : undefined
      }
    />
  );

  const netValueCalculatedFormatted = props.fullProductItem?.estimatedNetValueCalculated
    ? Utils.Mask.Money(props.fullProductItem.estimatedNetValueCalculated)
    : messageError;

  const netValueCalculatedComponent = (
    <ValueWithSubtitle
      value={netValueCalculatedFormatted}
      subtitle={props.fullProductItem?.estimatedNetValueCalculated ? 'No vencimento' : undefined}
    />
  );

  const title =
    props?.productDisplay ||
    props?.issuerName ||
    props?.productName ||
    props?.issuerFullName ||
    messageError;

  const productTitleProps = { ...props, title };

  const ProductItem = props.isMobileDevice
    ? { value: <ProductTitle {...productTitleProps} /> }
    : { label: 'Produto', value: title };

  if (props.IsSellOperation) {
    const redemptionDate = props?.redemptionDate
      ? Utils.Mask.DateFormat(props?.redemptionDate, 'dd/MM/yyyy')
      : messageError;

    return [
      ProductItem,
      { label: 'Tipo', value: <OperationType title="Resgate" /> },
      { label: 'Quantidade disponível', value: formatAvailableAmount(props?.availableAmount!) },
      { label: 'IR', value: Utils.Mask.Money(props?.ir) || messageError },
      { label: 'Valor líquido', value: Utils.Mask.Money(props?.netFinancialValue) || messageError },
      {
        label: 'Qtd. solicitada',
        value: formatAvailableAmount(props?.quantity || props?.orderQuantity!),
      },
      { label: 'Disponível em conta', value: redemptionDate },
    ];
  }

  return [
    { value: <ProductTitle {...productTitleProps} /> },
    { label: 'Risco', value: riskRatingLevelComponent },
    {
      label: 'IR',
      value: props.fullProductItem?.incomeTaxAmountNormalized || messageError,
    },
    {
      label: 'Rentabilidade',
      value: profitabilityComponent,
      mobileValue: profitabilityFormatted,
    },
    ...(props.isMobileDevice
      ? [
          { label: 'Validade', value: maturityDateFormatted },
          { label: 'Carência', value: dueDateFormatted },
        ]
      : [{ label: 'Prazo', value: timelinesComponent }]),
    {
      label: 'Taxa',
      labelDescription: '(Spread)',
      value:
        props.fullProductItem?.orderFee?.label ||
        props.fullProductItem?.orderFee?.customerFeeAmountFormatted ||
        messageError,
    },
    {
      label: 'Preço unitário',
      value: unitPriceFormatted,
    },
    { label: 'Fee', value: spreadFeeComponent, mobileValue: spreadFeeFormatted },
    { label: 'Valor aplicado', value: props.orderAmountFormatted },
    {
      label: 'Líquido estimado',
      value: netValueCalculatedComponent,
      preventLastHandler: true,
      mobileValue: netValueCalculatedFormatted,
    },
  ];
};

const createFundSection = (props: createProductSectionProps) => {
  const profitabilityTwelveMonthsAdjusted = props?.profitabilityTwelveMonthsAdjusted
    ? Utils.Mask.Percentage(props?.profitabilityTwelveMonthsAdjusted)
    : messageError;
  const administrationFee = props?.administrationFee
    ? props?.administrationFee.substring(0, 10) + '...' // Corrigir campo muito grande
    : messageError;
  const quotationRedemptionTermFormatted = props?.quotationRedemptionTermFormatted
    ? props?.quotationRedemptionTermFormatted
    : messageError;
  const riskRatingLevelComponent = props?.riskRatingLevel ? (
    <RiskRatingTooltip label={props?.riskRating} value={props?.riskRatingLevel} />
  ) : (
    messageError
  );

  const title = props?.issuerName || props?.productName || props?.fundName || messageError;

  const productTitleProps = { ...props, title };

  const ProductItem = props.isMobileDevice
    ? { value: <ProductTitle {...productTitleProps} /> }
    : { label: 'Produto', value: title };

  const getEstimatedNetValue = () => {
    if (
      props.orderAmount !== undefined &&
      props.orderAmount !== null &&
      props.expectedIR !== undefined &&
      props.expectedIR !== null
    ) {
      return Utils.Mask.Money(props.orderAmount - props.expectedIR);
    }
    return messageError;
  };

  if (props.IsSellOperation) {
    const estimatedNetValue = getEstimatedNetValue();

    const dateQuotation = props.fullProductItem?.dateQuotation;

    const quotationDate = dateQuotation ? dateQuotation?.quotationDate : props.quotationDate;
    const liquidationDate = dateQuotation ? dateQuotation?.liquidityDate : props.liquidationDate;

    const quotationDateFormatted = quotationDate
      ? Utils.Mask.DateFormat(quotationDate, 'dd/MM/yyyy')
      : messageError;

    const liquidationDateFormatted = liquidationDate
      ? Utils.Mask.DateFormat(liquidationDate, 'dd/MM/yyyy')
      : messageError;

    return [
      ProductItem,
      { label: 'Tipo', value: <OperationType title="Resgate" /> },
      { label: 'Valor bruto solicitado', value: props.orderAmountFormatted },
      { label: 'Valor líquido estimado', value: estimatedNetValue },
      { label: 'Data de cot. atual', value: quotationDateFormatted },
      { label: 'Data de liq. atual', value: liquidationDateFormatted },
    ];
  }

  return [
    ProductItem,
    { label: 'Tipo', value: props?.productCategory || messageError },
    { label: 'Rent. 12 meses', value: profitabilityTwelveMonthsAdjusted },
    { label: 'Taxa admin.', value: administrationFee },
    { label: 'Resgate', value: quotationRedemptionTermFormatted },
    { label: 'Risco', value: riskRatingLevelComponent },
    { label: 'Valor aplicado', value: props.orderAmountFormatted },
  ];
};
const createDirectTreasurySection = (props: createProductSectionProps) => {
  const title = props?.titleName || messageError;
  const productTitleProps = { ...props, title };

  const ProductItem = props.isMobileDevice
    ? { value: <ProductTitle {...productTitleProps} /> }
    : { label: 'Produto', value: title };
  const orderQuantity = `${props?.orderQuantity?.toFixed(2) || messageError}`;

  if (props.IsSellOperation) {
    const redemptionDate = props?.redemptionDate
      ? Utils.Mask.DateFormat(props?.redemptionDate, 'dd/MM/yyyy')
      : messageError;
    return [
      ProductItem,
      { label: 'Tipo', value: <OperationType title="Resgate" /> },
      { label: 'Valor estimado', value: props.orderAmountFormatted },
      { label: 'Qtd. solicitada', value: formatAvailableAmount(props?.orderQuantity!) },
      { label: 'Disponível em conta', value: redemptionDate },
    ];
  }

  const orderDate = props?.orderDate
    ? Utils.Mask.DateFormat(props?.orderDate, 'dd/MM/yyyy')
    : messageError;
  const settlementDate = props?.settlementDate
    ? Utils.Mask.DateFormat(props?.settlementDate, 'dd/MM/yyyy')
    : messageError;
  const maturityDate = props?.maturityDate
    ? Utils.Mask.DateFormat(props?.maturityDate, 'dd/MM/yyyy')
    : messageError;

  return [
    ProductItem,
    { label: 'Data da aplicação', value: orderDate },
    { label: 'Data de liquidação', value: settlementDate },
    { label: 'Vencimento', value: maturityDate },
    { label: 'Quantidade', value: orderQuantity },
    { label: 'Valor do investimento', value: props.orderAmountFormatted },
  ];
};

const createEquitySection = (props: createProductSectionProps) => {
  const getOperationText = (operation: OrderOperationType) => {
    return operation === 'buy' ? 'Compra' : 'Venda';
  };

  const productDisplayComponent = (
    <EquityProductDisplay
      src={props?.icons?.light}
      productName={props?.productName}
      symbol={props?.symbol}
    />
  );

  const lastPriceComponent = (
    <EquityLastPrice
      lastPrice={props?.lastPrice}
      priceChangeTax={props?.priceChangeTax}
      tradeDate={props?.tradeDate}
    />
  );

  const orderQuantity = props?.orderQuantity ? String(props?.orderQuantity) : messageError;
  const orderPrice = props?.orderPrice ? Utils.Mask.Money(props?.orderPrice) : messageError;
  const orderTypeDomain = props.fullProductItem?.orderType === 'Market' ? 'Market' : 'Limit';
  const orderTypeLabel = EQUITY_ORDER_TYPE_LABEL[orderTypeDomain];

  const productInfo = props.isMobileDevice
    ? [{ value: <EquityMobileTitle {...props} /> }]
    : [{ value: productDisplayComponent }, { label: 'Último preço', value: lastPriceComponent }];

  return [
    ...productInfo,
    {
      label: 'Operação',
      value: props.operationType ? (
        <OperationType title={getOperationText(props.operationType)} />
      ) : (
        messageError
      ),
    },
    { label: 'Validade da ordem', value: 'Hoje' },
    { label: 'Tipo da ordem', value: orderTypeLabel },
    { label: 'Quantidade', value: orderQuantity },
    { label: 'Preço por ativo', value: orderPrice },
    { label: 'Total estimado', value: props.orderAmountFormatted },
  ];
};

const createStructuredOperationsSection = (props: createProductSectionProps) => {
  const operationName =
    props?.fantasyName && props?.asset ? `${props?.fantasyName} ${props?.asset}` : messageError;
  const maturityDate = props?.maturityDate
    ? Utils.Mask.DateFormat(props?.maturityDate, 'dd/MM/yyyy')
    : messageError;
  const minimumApplication = props?.minimumApplication
    ? Utils.Mask.Money(props?.minimumApplication)
    : messageError;

  const suitabilityProductRiskComponent = props?.suitabilityProductRisk?.scoreAdjusted ? (
    <RiskRatingTooltip
      label={props?.suitabilityProductRisk?.risk}
      value={props?.suitabilityProductRisk?.scoreAdjusted}
    />
  ) : (
    messageError
  );

  const productTitleProps = { ...props, title: operationName };

  const Operation = props.isMobileDevice
    ? { value: <ProductTitle {...productTitleProps} /> }
    : { label: 'Produto', value: operationName };

  return [
    Operation,
    { label: 'Estratégia', value: props?.strategy || messageError },
    { label: 'Risco', value: suitabilityProductRiskComponent },
    { label: 'Vencimento', value: maturityDate },
    { label: 'Aplicação em', value: props?.applicationIn || messageError },
    { label: 'Aplicação mínima', value: minimumApplication },
    { label: 'Tipo de boletagem', value: props?.billingType?.billingDescription || messageError },
    { label: 'Valor aplicado', value: props.orderAmountFormatted },
  ];
};
